// src/hooks/useAnnotationLayer.js
//
// RESPONSIBILITIES (narrowed):
//   - Manages the LIVE DRAWING SESSION only:
//       "annotation-committed"  — features drawn in the current open session
//       "annotation-pending"    — rubber-band preview (pts + live cursor)
//
// Saved drawn layers are handled by the existing geojson-inline path in
// useSyncRegistryLayers — exactly the same as imported layers. No conflict.
//
// When the panel closes / session is saved, annotation-committed is cleared.

import { useEffect, useRef } from "react";
import { useAnnotationStore } from "../stores/useAnnotationStore";
import { useLayerRegistryStore } from "../stores/useLayerRegistryStore";

const SOURCE_COMMITTED = "annotation-committed";
const SOURCE_PENDING = "annotation-pending";

const LAYER_POLY_FILL = "annotation-poly-fill";
const LAYER_POLY_LINE = "annotation-poly-line";
const LAYER_LINE = "annotation-line";
const LAYER_POINT = "annotation-point";
const LAYER_NOTE_LABEL = "annotation-note-label";
const LAYER_FEATURE_LABEL = "annotation-feature-label";
const LAYER_SELECTED_LINE = "annotation-selected-line";
const LAYER_SELECTED_POINT = "annotation-selected-point";
const LAYER_PENDING = "annotation-pending-line";

// Annotation types MlDraw takes ownership of while they are being edited —
// the ones it can put vertex handles on. The counterpart to
// EDITABLE_GEOMETRY in AnnotationGeometryEditor, in this module's
// vocabulary (annotation type) rather than GeoJSON's (geometry type).
const MLDRAW_OWNED = new Set(["polygon", "line"]);

// Point types are dragged directly here instead: one coordinate, so vertex
// handles would be a heavier mechanism than the thing they edit.
const DRAGGABLE_LAYERS = [LAYER_POINT, LAYER_NOTE_LABEL];

export function useAnnotationLayer(map, mapLoaded) {
  const initializedRef = useRef(false);
  const cursorRef = useRef(null);

  const annotations = useAnnotationStore(s => s.annotations);
  const pending = useAnnotationStore(s => s.pendingPoints);
  const open = useAnnotationStore(s => s.open);
  // Subscribed, not read from getState(), because the crosshair effect below
  // has to re-run when the armed tool changes.
  const tool = useAnnotationStore(s => s.tool);
  const selectedId = useAnnotationStore(s => s.selectedId);
  const editingId = useAnnotationStore(s => s.editingId);

  // ── INIT SOURCES + LAYERS (once) ─────────────────────────────────
  useEffect(() => {
    if (!map || !mapLoaded || initializedRef.current) return;
    initializedRef.current = true;

    if (!map.getSource(SOURCE_COMMITTED)) {
      map.addSource(SOURCE_COMMITTED, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    if (!map.getSource(SOURCE_PENDING)) {
      map.addSource(SOURCE_PENDING, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    // Polygon fill
    if (!map.getLayer(LAYER_POLY_FILL)) {
      map.addLayer({
        id: LAYER_POLY_FILL,
        type: "fill",
        source: SOURCE_COMMITTED,
        filter: ["==", ["get", "annotationType"], "polygon"],
        paint: { "fill-color": ["get", "color"], "fill-opacity": 0.15 },
      });
    }

    // Polygon + line strokes
    if (!map.getLayer(LAYER_POLY_LINE)) {
      map.addLayer({
        id: LAYER_POLY_LINE,
        type: "line",
        source: SOURCE_COMMITTED,
        filter: ["in", ["get", "annotationType"], ["literal", ["polygon", "line"]]],
        paint: { "line-color": ["get", "color"], "line-width": 2, "line-opacity": 0.9 },
      });
    }

    // Standalone lines
    if (!map.getLayer(LAYER_LINE)) {
      map.addLayer({
        id: LAYER_LINE,
        type: "line",
        source: SOURCE_COMMITTED,
        filter: ["==", ["get", "annotationType"], "line"],
        paint: { "line-color": ["get", "color"], "line-width": 2.5, "line-opacity": 1 },
      });
    }

    // Pins — full size circle
    if (!map.getLayer(LAYER_POINT)) {
      map.addLayer({
        id: LAYER_POINT,
        type: "circle",
        source: SOURCE_COMMITTED,
        filter: ["==", ["get", "annotationType"], "pin"],
        paint: {
          "circle-radius": 6,
          "circle-color": ["get", "color"],
          "circle-opacity": 0.9,
          "circle-stroke-color": "#000",
          "circle-stroke-width": 1.5,
          "circle-stroke-opacity": 0.6,
        },
      });
    }

    // ── NO BUBBLE ────────────────────────────────────────────────────────
    //
    // A note used to render a 🗨 glyph with the text floating above it. The
    // bubble carried no information the text did not — it marked the anchor
    // point, which the text now sits on directly — and at 20px it was the
    // largest thing on the map for the shortest annotation.
    //
    // THE LABEL INHERITS ITS JOB. LAYER_NOTE_DOT was in DRAGGABLE_LAYERS and
    // SELECTABLE, so deleting it outright would have left notes unselectable
    // and undraggable, which is the kind of thing that reads as an unrelated
    // regression a week later. LAYER_NOTE_LABEL takes both roles: the text is
    // the hit target now, which is also the thing a user would think to click.

    // Note text — white with dark halo for legibility on any basemap
    if (!map.getLayer(LAYER_NOTE_LABEL)) {
      map.addLayer({
        id: LAYER_NOTE_LABEL,
        type: "symbol",
        source: SOURCE_COMMITTED,
        filter: ["==", ["get", "annotationType"], "note"],
        layout: {
          "text-field": ["get", "label"],
          "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
          "text-size": 13,
          // Centred on the coordinate rather than floated above a glyph that no
          // longer exists. The old [0,-1.6]/bottom pair positioned the text
          // clear of the bubble; with the bubble gone it would hover 1.6em
          // above the point it labels.
          "text-offset": [0, 0],
          "text-anchor": "center",
          "text-max-width": 12,
          // Was false, which let the label drop out under collision — fine when
          // a bubble still marked the spot, and a vanishing annotation now.
          "text-allow-overlap": true,
          "icon-allow-overlap": true,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "rgba(15,23,42,0.85)",   // slate-900
          "text-halo-width": 3,
          "text-halo-blur": 0,                         // zero blur = hard edge = box-like
        },
      });
    }

    // Selection halo — one layer over every geometry type. Drawn as a wide,
    // low-opacity stroke beneath nothing in particular; circle-stroke would
    // not reach lines and polygons, and three per-type highlight layers
    // would all need the same filter kept in step.
    if (!map.getLayer(LAYER_SELECTED_LINE)) {
      map.addLayer({
        id: LAYER_SELECTED_LINE,
        type: "line",
        source: SOURCE_COMMITTED,
        filter: ["==", ["get", "selected"], true],
        paint: {
          "line-color": "#ffffff",
          "line-width": 6,
          "line-opacity": 0.35,
          "line-blur": 1,
        },
      });
    }
    if (!map.getLayer(LAYER_SELECTED_POINT)) {
      map.addLayer({
        id: LAYER_SELECTED_POINT,
        type: "circle",
        source: SOURCE_COMMITTED,
        filter: ["all",
          ["==", ["get", "selected"], true],
          ["in", ["get", "annotationType"], ["literal", ["pin", "note"]]],
        ],
        paint: {
          "circle-radius": 11,
          "circle-color": "#ffffff",
          "circle-opacity": 0.25,
        },
      });
    }

    // Labels for NAMED pins, lines and areas. Filtered on `named` so the
    // map does not fill up with "Pin 1, Pin 2, Pin 3" — an auto-label is a
    // handle for the panel list, not something the user asked to see on the
    // map. Notes are excluded because LAYER_NOTE_LABEL already draws them,
    // and rendering both would double up.
    if (!map.getLayer(LAYER_FEATURE_LABEL)) {
      map.addLayer({
        id: LAYER_FEATURE_LABEL,
        type: "symbol",
        source: SOURCE_COMMITTED,
        filter: ["all",
          ["==", ["get", "named"], true],
          ["!=", ["get", "annotationType"], "note"],
        ],
        layout: {
          "text-field": ["get", "label"],
          "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
          "text-size": 12,
          "text-offset": [0, -1.2],
          "text-anchor": "bottom",
          "text-max-width": 12,
          "text-allow-overlap": false,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "rgba(15,23,42,0.85)",
          "text-halo-width": 3,
          "text-halo-blur": 0,
        },
      });
    }

    // Rubber-band pending line
    if (!map.getLayer(LAYER_PENDING)) {
      map.addLayer({
        id: LAYER_PENDING,
        type: "line",
        source: SOURCE_PENDING,
        paint: {
          // accent-teal. The rubber band is the only thing on screen that is
          // NOT yet an annotation, so it should not look like one — white read
          // as a finished white-coloured line against imagery, and the palette
          // already uses teal for in-progress state elsewhere.
          "line-color": "#16f2d4",
          "line-width": 1.5,
          "line-dasharray": [3, 2],
          "line-opacity": 0.9,
        },
      });
    }
  }, [map, mapLoaded]);

  // ── RUBBER-BAND: mousemove updates pending source live ────────────
  useEffect(() => {
    if (!map || !mapLoaded) return;

    function onMouseMove(e) {
      cursorRef.current = [e.lngLat.lng, e.lngLat.lat];

      const { open, tool, pendingPoints } = useAnnotationStore.getState();
      if (!open || !tool) return;
      if (tool !== "polygon" && tool !== "line") return;
      if (pendingPoints.length === 0) return;

      const src = map.getSource(SOURCE_PENDING);
      if (!src) return;

      src.setData({
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: [...pendingPoints, cursorRef.current],
          },
        }],
      });
    }

    map.on("mousemove", onMouseMove);
    return () => map.off("mousemove", onMouseMove);
  }, [map, mapLoaded]);

  // ── CROSSHAIR WHILE A TOOL IS ARMED ─────────────────────────────────────
  //
  // The map's default cursor is a grab hand, which says "drag me" — the exact
  // opposite of what an armed draw tool wants, and the reason it was not
  // obvious that a click would place something.
  //
  // ALL FOUR TOOLS, including note. It was asked for on point, line and poly,
  // but a note is placed by clicking the map like the others, and leaving it
  // as the one tool with a grab hand would be an inconsistency nobody could
  // explain.
  //
  // A BODY CLASS, NOT AN INLINE STYLE, AND THE FIRST CUT GOT THAT WRONG.
  //
  // Setting canvas.style.cursor from here does not survive: THIS HOOK'S OWN
  // hover handlers reset it to "" on every mouseleave over a selectable layer
  // (onLeave, below), and WellLayer, SiteLayer, PadLayer, PadAccessRoadLayer,
  // CellMap and the registry layers all do the same on their own features. An
  // inline write cannot win against handlers that fire on every mouse event —
  // which is what QcPolygonDrawer already discovered and wrote down, and what
  // the rule in styles/maplibre-draw.css exists for.
  //
  // The class wins for as long as it is on, costs nothing per frame, and needs
  // no cooperation from the handlers that were stomping it.
  //
  // ALL FOUR TOOLS, including note. It was asked for on point, line and poly,
  // but a note is placed by clicking the map like the others, and leaving it as
  // the one tool with a grab hand would be an inconsistency nobody could
  // explain.
  useEffect(() => {
    if (!(open && tool)) return;
    document.body.classList.add("annotation-drawing");
    return () => document.body.classList.remove("annotation-drawing");
  }, [open, tool]);


  // ── SYNC COMMITTED → map (live session annotations only) ──────────
  //
  // The annotation whose geometry MlDraw currently owns is withheld: MlDraw
  // paints it into its own ml-draw-final source while editing, and drawing
  // it here too would stack a stale copy under the live one — the edit
  // would look like it was doing nothing until commit.
  //
  // The type check is load-bearing, not defensive. editingId covers every
  // type, but MlDraw only claims the ones it can vertex-edit — areas and
  // lines (EDITABLE_GEOMETRY in AnnotationGeometryEditor). Withholding on
  // editingId alone would hide a pin or note that nothing else is drawing,
  // and the feature would simply vanish the moment the user hit the pencil.
  // Withholding too little is the opposite fault: a stale copy stacked
  // under MlDraw's live one, so the edit appears to do nothing.
  //
  // `selected` rides along as a feature property so the highlight layer can
  // filter on it without a second source.
  useEffect(() => {
    if (!map || !mapLoaded || !map.getSource(SOURCE_COMMITTED)) return;
    map.getSource(SOURCE_COMMITTED).setData({
      type: "FeatureCollection",
      features: annotations
        .filter(a => !(a.id === editingId && MLDRAW_OWNED.has(a.type)))
        .map(a => ({
          ...a.geojson,
          id: a.id,
          properties: { ...a.geojson.properties, id: a.id, selected: a.id === selectedId },
        })),
    });
  }, [map, mapLoaded, annotations, selectedId, editingId]);

  // ── THE SAVED COPY YIELDS WHILE ITS SESSION COPY IS LIVE ──────────
  //
  // A layer opened from My Layers is rendered TWICE: once by the registry
  // as `user-layer-<id>-fill`/`-line`, which is the persisted geometry, and
  // once here as the editable session. Both drawn at once means the
  // pre-edit shape sits underneath the one being dragged, so edits look
  // like they are doing nothing — the same fault as failing to withhold
  // the feature MlDraw owns, one level up.
  //
  // Capturing the pre-edit visibility is the load-bearing half. Saving an
  // edit re-registers the layer, and registerLayers recomputes `visible`
  // from the incoming definition (useLayerRegistryStore.js:36), which
  // buildRegistryLayers declares as false — so the registry forgets that
  // the user had this layer switched on. Without the captured value there
  // would be nothing to restore from, and a layer you opened, edited and
  // closed would come back invisible.
  //
  // The hide itself is re-asserted rather than set once, so it survives any
  // re-registration that ever declares a visible layer. The "only if
  // currently visible" guard stops that from looping: the second pass finds
  // nothing to do.
  const editingLayerId = useAnnotationStore(s => s.editingLayerId);
  const registryLayers = useLayerRegistryStore(s => s.layers);
  const preEditVisibilityRef = useRef(null);

  useEffect(() => {
    const reg = useLayerRegistryStore.getState();
    const idsFor = (layerId) => [
      `user-layer-${layerId}-fill`,
      `user-layer-${layerId}-line`,
    ];

    const restore = () => {
      const held = preEditVisibilityRef.current;
      if (!held) return;
      for (const [id, wasVisible] of Object.entries(held.visibility)) {
        if (reg.layers[id]) reg.setLayerVisibilityById(id, wasVisible);
      }
      preEditVisibilityRef.current = null;
    };

    if (!editingLayerId) { restore(); return; }

    // Switching directly from editing one layer to another: give the first
    // one back before claiming the second.
    if (preEditVisibilityRef.current &&
        preEditVisibilityRef.current.layerId !== editingLayerId) {
      restore();
    }

    const ids = idsFor(editingLayerId);
    if (!preEditVisibilityRef.current) {
      preEditVisibilityRef.current = {
        layerId: editingLayerId,
        visibility: Object.fromEntries(
          ids.map(id => [id, !!reg.layers[id]?.visible])
        ),
      };
    }

    for (const id of ids) {
      if (reg.layers[id]?.visible) reg.setLayerVisibilityById(id, false);
    }
  }, [editingLayerId, registryLayers]);

  // ── DRAG A PIN / NOTE TO MOVE IT ──────────────────────────────────
  //
  // Hand-rolled rather than routed through MlDraw, which has no Point mode
  // at all — a point is one coordinate, so the whole gesture is mousedown
  // to claim, mousemove to write, mouseup to release.
  //
  // Dragging is scoped to the annotation the user has opened for editing,
  // not to any pin under the cursor. The pencil then means one consistent
  // thing for every type — "this feature is now live" — and it removes the
  // hazard of nudging a finished pin while clicking around the map. It is
  // also the only way a pin's edit state is visible at all: a polygon grows
  // vertex handles, but a draggable pin looks exactly like a fixed one.
  //
  // Two things this must not break:
  //   - map panning. mousedown on a feature calls preventDefault() so
  //     maplibre's drag-pan never starts; without it the map slides away
  //     under the pin.
  //   - the draw tools. A drag while a tool is armed would fight the click
  //     handler in MapView, so dragging is only live when no tool is
  //     selected. Selecting a tool is how the user says "I am placing, not
  //     editing" (the store clears editingId on setTool for the same
  //     reason).
  useEffect(() => {
    if (!map || !mapLoaded) return;

    let draggingId = null;

    const draggableId = () => {
      const s = useAnnotationStore.getState();
      return s.open && !s.tool ? s.editingId : null;
    };

    function onEnter(e) {
      if (e.features?.[0]?.properties?.id === draggableId()) {
        map.getCanvas().style.cursor = "move";
      }
    }
    function onLeave() {
      if (!draggingId) map.getCanvas().style.cursor = "";
    }

    function onDown(e) {
      const f = e.features?.[0];
      const id = f?.properties?.id;
      if (!id || id !== draggableId()) return;

      e.preventDefault();                 // stop maplibre's drag-pan
      draggingId = id;
      useAnnotationStore.getState().selectAnnotation(id);
      map.getCanvas().style.cursor = "grabbing";
      map.on("mousemove", onMove);
      map.once("mouseup", onUp);
    }

    function onMove(e) {
      if (!draggingId) return;
      useAnnotationStore.getState().updateAnnotationGeometry(draggingId, {
        type: "Point",
        coordinates: [e.lngLat.lng, e.lngLat.lat],
      });
    }

    function onUp() {
      draggingId = null;
      map.off("mousemove", onMove);
      map.getCanvas().style.cursor = "";
    }

    DRAGGABLE_LAYERS.forEach(id => {
      map.on("mouseenter", id, onEnter);
      map.on("mouseleave", id, onLeave);
      map.on("mousedown", id, onDown);
    });

    return () => {
      DRAGGABLE_LAYERS.forEach(id => {
        map.off("mouseenter", id, onEnter);
        map.off("mouseleave", id, onLeave);
        map.off("mousedown", id, onDown);
      });
      map.off("mousemove", onMove);
    };
  }, [map, mapLoaded]);

  // ── CLICK A FEATURE TO SELECT IT ──────────────────────────────────
  // Only when no draw tool is armed, so a click meant to place a pin is
  // never swallowed by a selection. Marks the event handled for the same
  // reason MapView's annotation click does — otherwise the click falls
  // through to well selection underneath.
  useEffect(() => {
    if (!map || !mapLoaded) return;

    const SELECTABLE = [LAYER_POINT, LAYER_NOTE_LABEL, LAYER_LINE, LAYER_POLY_FILL];

    function onClick(e) {
      const s = useAnnotationStore.getState();
      if (!s.open || s.tool) return;
      const id = e.features?.[0]?.properties?.id;
      if (!id) return;
      e.originalEvent._annotationHandled = true;
      s.selectAnnotation(id);
    }

    SELECTABLE.forEach(id => map.on("click", id, onClick));
    return () => SELECTABLE.forEach(id => map.off("click", id, onClick));
  }, [map, mapLoaded]);

  // ── CLEAR PENDING when drawing cancelled or committed ─────────────
  useEffect(() => {
    if (!map || !mapLoaded || !map.getSource(SOURCE_PENDING)) return;
    if (pending.length === 0 || !open) {
      map.getSource(SOURCE_PENDING).setData({ type: "FeatureCollection", features: [] });
    }
  }, [map, mapLoaded, pending, open]);

  // ── CLEAR SESSION on panel close ──────────────────────────────────
  useEffect(() => {
    if (!map || !mapLoaded || !map.getSource(SOURCE_COMMITTED)) return;
    if (!open) {
      map.getSource(SOURCE_COMMITTED).setData({ type: "FeatureCollection", features: [] });
    }
  }, [map, mapLoaded, open]);

  // ── CLEANUP on unmount ────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (!map) return;
      [LAYER_POLY_FILL, LAYER_POLY_LINE, LAYER_LINE, LAYER_POINT,
        LAYER_NOTE_LABEL, LAYER_FEATURE_LABEL,
        LAYER_SELECTED_LINE, LAYER_SELECTED_POINT, LAYER_PENDING].forEach(id => {
          try { if (map.getLayer(id)) map.removeLayer(id); } catch { /* style already torn down */ }
        });
      [SOURCE_COMMITTED, SOURCE_PENDING].forEach(id => {
        try { if (map.getSource(id)) map.removeSource(id); } catch { /* still referenced by a layer */ }
      });
      initializedRef.current = false;
    };
  }, [map]);
}